import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { getTeachersApi, getTeacherByIdApi, followTeacherApi, unfollowTeacherApi } from '../../api/teachers';

// ──────────────────────────────────────────────
// Async Thunks
// ──────────────────────────────────────────────

export const fetchTeachers = createAsyncThunk(
  'teachers/fetchTeachers',
  async (params = {}, { rejectWithValue }) => {
    try {
      return await getTeachersApi(params);
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed to fetch teachers' });
    }
  }
);

export const fetchTeacherById = createAsyncThunk(
  'teachers/fetchTeacherById',
  async (id, { rejectWithValue }) => {
    try {
      return await getTeacherByIdApi(id);
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed to fetch teacher' });
    }
  }
);

export const toggleFollowTeacher = createAsyncThunk(
  'teachers/toggleFollowTeacher',
  async ({ id, isFollowing }, { rejectWithValue }) => {
    try {
      if (isFollowing) {
        return await unfollowTeacherApi(id);
      } else {
        return await followTeacherApi(id);
      }
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed to update follow status' });
    }
  }
);

// ──────────────────────────────────────────────
// Slice
// ──────────────────────────────────────────────

const teachersSlice = createSlice({
  name: 'teachers',
  initialState: {
    teachers: [],
    currentTeacher: null,
    pagination: {
      total: 0,
      page: 1,
      limit: 10,
      pages: 0,
    },
    loading: false,
    error: null,
    filters: {
      search: '',
      language: '',
    },
  },
  reducers: {
    setTeacherSearch: (state, action) => {
      state.filters.search = action.payload;
    },
    setTeacherLanguage: (state, action) => {
      state.filters.language = action.payload;
    },
    clearTeacherFilters: (state) => {
      state.filters = { search: '', language: '' };
    },
    clearTeacherError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // ── fetchTeachers ──
      .addCase(fetchTeachers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTeachers.fulfilled, (state, action) => {
        state.loading = false;
        state.teachers = action.payload.data || [];
        state.pagination = action.payload.pagination || state.pagination;
      })
      .addCase(fetchTeachers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Failed to fetch teachers';
      })
      // ── fetchTeacherById ──
      .addCase(fetchTeacherById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTeacherById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentTeacher = action.payload.data || action.payload || null;
      })
      .addCase(fetchTeacherById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Failed to fetch teacher details';
      })
      // ── toggleFollowTeacher ──
      .addCase(toggleFollowTeacher.fulfilled, (state, action) => {
        // Optimistically update the follow status in currentTeacher
        if (state.currentTeacher) {
          const isNowFollowing = !state.currentTeacher.is_follow;
          state.currentTeacher.is_follow = isNowFollowing;
          state.currentTeacher.follower_count += isNowFollowing ? 1 : -1;
        }
      });
  },
});

export const {
  setTeacherSearch,
  setTeacherLanguage,
  clearTeacherFilters,
  clearTeacherError,
} = teachersSlice.actions;

export default teachersSlice.reducer;
